import * as Select from '@radix-ui/react-select';
import { forwardRef, type JSX, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { DropdownTriggerClearButton } from '~/chords/DropdownTreeSingleSelect/components/DropdownTriggerClearButton';
import { chevron } from '~/chords/DropdownTreeSingleSelect/styles';
import type {
  DropdownClearOptionsInternal,
  DropdownState,
  DropdownTriggerVariant,
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownTreeSingleSelect/types';
import { Icon } from '~/chords/Icon';
import { cn } from '~/chords/lib/cn';

interface DropdownTriggerProps extends RadixUiProps {
  id?: string;
  placeholder?: string;
  selectedOption?: InternalDropdownOption;
  isOpen: boolean;
  state?: DropdownState;
  variant?: DropdownTriggerVariant;
  disabled?: boolean;
  OptionTemplate?: OptionTemplateComponent;
  clearOptions: DropdownClearOptionsInternal;
}

export const DropdownTrigger = forwardRef<
  HTMLButtonElement,
  DropdownTriggerProps
>(
  (
    {
      id,
      placeholder,
      selectedOption,
      isOpen,
      state,
      variant = 'default',
      disabled,
      OptionTemplate,
      clearOptions,
      radix = {},
    },
    ref,
  ) => {
    const { t } = useTranslation('common');

    const placeholderText = placeholder || t('dropdownTree.placeholder');
    let valueDisplay: JSX.Element | ReactNode = placeholderText;

    if (selectedOption) {
      if (OptionTemplate) {
        valueDisplay = (
          <OptionTemplate option={selectedOption} isTrigger={true} />
        );
      } else {
        valueDisplay = (
          <span className="truncate text-default">
            {selectedOption.label || selectedOption.value}
          </span>
        );
      }
    }

    const isDisabled = disabled ?? radix.select?.disabled;

    const showClearButton =
      clearOptions?.isEnabled &&
      (clearOptions.placements.includes('trigger') ||
        clearOptions.placements.includes('default')) &&
      selectedOption &&
      !isDisabled;

    const ClearButton =
      clearOptions?.TriggerTemplate ?? DropdownTriggerClearButton;

    return (
      <div className="group/dropdown-trigger relative">
        <Select.Trigger
          {...radix.selectTrigger}
          ref={ref}
          id={id}
          className={cn(
            'peer relative flex h-full w-full cursor-pointer items-stretch justify-between gap-sm rounded-xs py-xs pr-xs text-body-sm leading-8',
            'outline outline-1 transition-colors-with-outline duration-300 ease-out bg-emphasis-default outline-emphasis-subtle',
            'enabled:hover:bg-emphasis-subtle enabled:hover:outline-emphasis-strong disabled:cursor-not-allowed disabled:outline-transparent',
            {
              'rounded-lg bg-transparent outline-transparent':
                variant === 'rounded-ghost',
            },
            {
              'pl-md': !(OptionTemplate && selectedOption),
              'outline-error-default': state === 'error',
              'outline-success-default': state === 'success',
              'outline-warning-default': state === 'warning',
            },
            radix.selectTrigger?.className,
          )}
          aria-expanded={isOpen}
          aria-invalid={state === 'error'}
        >
          <Select.Value
            {...radix.selectValue}
            placeholder={
              <div className="flex min-w-0 items-center text-subtle">
                <div className="truncate">{placeholderText}</div>
              </div>
            }
            asChild
          >
            <div
              className={cn(
                {
                  'text-disabled': isDisabled,
                },
                'flex items-center truncate',
                radix.selectValue?.className,
              )}
            >
              {valueDisplay}
            </div>
          </Select.Value>
          <div className="flex items-center">
            {showClearButton ? (
              // Reserves space for the clear button.
              <div
                aria-hidden
                className="w-[0] transition-[width] delay-200 duration-100 group-focus-within/dropdown-trigger:w-xl group-hover/dropdown-trigger:w-xl"
              />
            ) : null}
            <Select.Icon
              {...radix.selectIcon}
              className={cn(
                'flex items-center text-default',
                radix.selectIcon?.className,
                {
                  'text-disabled': isDisabled,
                },
              )}
            >
              <div
                className={cn(
                  chevron({ state: isOpen ? 'open' : 'default' }),
                  'w-xl',
                )}
              >
                <Icon size="md" type="chevron-down" />
              </div>
            </Select.Icon>
          </div>
        </Select.Trigger>
        {showClearButton ? (
          <ClearButton onClear={clearOptions?.onClear} />
        ) : null}
      </div>
    );
  },
);

DropdownTrigger.displayName = 'DropdownTrigger';
